<!--
  @MasterTradePanel  Left panel of the page "Handeln" (master view): all heroes of the game with their cash,
                     quick cash adjustment and their wish lists (Merkliste). A wish list opens in play/TradeDesk.svelte.
-->

<script module>
		/** hero whose wish list is shown in the main area (null = loot management of play/Obtain.svelte) **/
		export const desk = $state({heroID: null})
</script>

<script>
		import {user, game, send_data} from "../client.svelte.js";
		import {money_label} from "../core.js";
		import {marked_entries} from "../play/Shop.svelte";

		const sourceTags = {merchant: "Händler", found: "Fund", companion: "Gefährte"}

		let heroes = $derived(game.heroes.map(heroID => user.characters.get(heroID)).filter(Boolean)
				.sort((a, b) => a.name.localeCompare(b.name, 'de')))
		let entries = $derived(Object.fromEntries(heroes.map(hero => [hero.ID, marked_entries(hero)])))
		let openTotal = $derived(Object.values(entries).reduce((sum, list) => sum + list.length, 0))

		/** gold and silver of a quick cash adjustment (negative values decrease the cash) **/
		class CashAdjust {
				gold = $state(null)
				silver = $state(null)
		}

		let opens = $state({})			// heroes with expanded wish list
		const adjusts = {}				// cash adjustment of each hero
		const bulk = new CashAdjust()	// cash adjustment for all heroes

		function adjust_of(heroID) {
				return adjusts[heroID] ??= new CashAdjust()
		}

		/** changes the cash of a hero ('each' = all heroes) by the entered gold and silver (1 gold = 100 silver) **/
		function change_cash(heroID, adjust) {
				const delta = Math.round((+adjust.gold || 0) * 100 + (+adjust.silver || 0))
				if (delta)
						send_data(`possession/${heroID}$silver`, {delta}, 'PATCH')
				adjust.gold = adjust.silver = null
		}

		function summary(list) {
				const counts = Object.groupBy(list, entry => entry.source)
				return Object.keys(sourceTags).filter(source => counts[source]).map(source => `${counts[source].length} ${sourceTags[source]}`).join(" · ")
		}
</script>


<!-------------------------------- HTML -------------------------------------->

<div class="masterTrade">

	<div class="panelhead">
		<h2>Helden</h2>
		<span class="muted">{openTotal} {(openTotal === 1) ? "offene Vormerkung" : "offene Vormerkungen"}</span>
	</div>

	{#each heroes as hero (hero.ID)}
		{@const list = entries[hero.ID]}
		{@const gold = user.possessions.get(`${hero.ID}$gold`)?.amount ?? 0}
		{@const silver = user.possessions.get(`${hero.ID}$silver`)?.amount ?? 0}
		{@const adjust = adjust_of(hero.ID)}
		<section class="card hero" class:selected={desk.heroID === hero.ID} aria-label={hero.name}>
			<div class="cash">
				<img class="portrait" src={hero.tokenURL} alt="">
				<span class="name">{hero.name}</span>
				<div class="coin">
					<span class="kicker"><span class="dot gold"></span>Gold</span>
					<span class="num">{gold}</span>
				</div>
				<div class="coin">
					<span class="kicker"><span class="dot silver"></span>Silber</span>
					<span class="num">{silver}</span>
				</div>
				{@render cashAdjust(hero.ID, adjust, hero.name)}
			</div>

			<div class="marklist">
				<button class="toggle" aria-expanded={!!opens[hero.ID]} onclick={() => opens[hero.ID] = !opens[hero.ID]}>
					<span class="kicker">Merkliste</span>
					<span class="count">{list.length}</span>
					<span class="summary">{summary(list)}</span>
					<i class="fa icon-chevron-down" class:open={opens[hero.ID]}></i>
				</button>
				{#if opens[hero.ID]}
					{#if list.length === 0}
						<p class="nothing">Nichts vorgemerkt.</p>
					{:else}
						<button class="items" aria-label="Merkliste von {hero.name} im Hauptbereich anzeigen" onclick={() => desk.heroID = hero.ID}>
							<span class="rows">
								{#each list as {poss, item, source, price} (poss.ID)}
									<span class="row">
										<span class="tag {source}">{sourceTags[source]}</span>
										<span class="itemname">{item.name}</span>
										<span class="price">{(source === 'found') ? "frei" : money_label(price * (poss.delta || 1))}</span>
									</span>
								{/each}
							</span>
							<span class="show">
								{(desk.heroID === hero.ID) ? "Im Hauptbereich geöffnet" : "Im Hauptbereich bearbeiten"}
								<i class="fa icon-arrow-right"></i>
							</span>
						</button>
					{/if}
				{/if}
			</div>
		</section>
	{/each}

	<section class="card cash bulk" aria-labelledby="bulk-head">
		<h2 id="bulk-head" class="kicker">Für jeden Helden</h2>
		{@render cashAdjust('each', bulk, null)}
	</section>

	{#if desk.heroID}
		<button class="loot" onclick={() => desk.heroID = null}>
			<i class="fa icon-list"></i>Findbare Gegenstände verwalten
		</button>
	{/if}

</div>

{#snippet cashAdjust(heroID, adjust, name)}
	{@const label = (name) ? `Bargeld von ${name} ändern` : "Bargeld jedes Helden ändern"}
	<button class="apply" aria-label={label} title="{label} (negative Werte ziehen ab)"
	        onclick={() => change_cash(heroID, adjust)}><i class="fa icon-check"></i></button>
	<input type="number" step="1" placeholder="GS" bind:value={adjust.gold} aria-label={(name) ? `Gold für ${name}` : "Gold für jeden Helden"}
	       onkeydown={event => (event.key === 'Enter') && change_cash(heroID, adjust)}>
	<input type="number" step="1" placeholder="SM" bind:value={adjust.silver} aria-label={(name) ? `Silber für ${name}` : "Silber für jeden Helden"}
	       onkeydown={event => (event.key === 'Enter') && change_cash(heroID, adjust)}>
{/snippet}


<!-------------------------------- STYLE -------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	/* starts below the main menu button (which reaches into the control area) */
	.masterTrade
		display: flex
		flex-direction: column
		gap: 12px
		margin-top: 48px
		max-height: calc(100vh - 132px)
		overflow-y: auto
		scrollbar-width: none

	.panelhead
		display: flex
		justify-content: space-between
		align-items: baseline
		padding: 0 4px

		h2
			margin: 0
			font-family: var(--font-heading, "EB Garamond", Georgia, serif)
			font-size: 22px

	.muted
		font-size: 13px
		color: colors.$grey-60

	.kicker
		display: flex
		align-items: center
		gap: 5px
		margin: 0
		font-size: 11px
		font-weight: normal
		letter-spacing: .08em
		text-transform: uppercase
		color: colors.$grey-85

	.card
		display: flex
		flex-direction: column
		gap: 12px
		padding: 14px 16px
		border: 1px solid colors.$grey-30
		border-radius: 10px
		background: colors.$grey-15

		&.selected
			border-color: colors.$primary-100
			box-shadow: inset 3px 0 0 colors.$primary-100

	/* — hero head with cash: portrait over two rows, adjustment inputs below gold and silver — */
	.cash
		display: grid
		grid-template-columns: auto 1fr 64px 64px
		align-items: center
		gap: 8px 10px

	.bulk
		grid-template-columns: 1fr auto 64px 64px

	.portrait
		grid-row: span 2
		align-self: stretch
		width: 84px
		height: 100%
		min-height: 0
		object-fit: cover
		border: 1px solid colors.$primary-100
		border-radius: 8px
		background: colors.$primary-040-black

	.name
		flex: 1
		min-width: 0
		font-family: var(--font-heading, "EB Garamond", Georgia, serif)
		font-size: 19px
		font-weight: bold
		line-height: 1.2

	.coin
		display: flex
		flex-direction: column
		align-items: flex-end
		line-height: 1.1

		.num
			font-family: var(--font-heading, "EB Garamond", Georgia, serif)
			font-size: 24px
			font-weight: bold

	.dot
		width: 8px
		height: 8px
		border-radius: 50%

		&.gold
			background: colors.$highlight-yellow-100

		&.silver
			background: colors.$grey-85

	/* — quick cash adjustment — */
	.cash
		.apply, input
			height: 40px
			margin: 0
			font-family: inherit

		.apply:focus-visible, input:focus-visible
			outline: 2px solid colors.$primary
			outline-offset: 2px

		.apply
			justify-self: end
			width: 44px
			border: 1px solid colors.$primary-100
			border-radius: 6px
			background: colors.$primary-040-black
			color: colors.$white
			font-size: 16px
			cursor: pointer

			&:hover
				background: colors.$primary-100

		input
			width: 100%
			min-width: 0
			padding: 0 8px
			border: 1px solid colors.$grey-30
			border-radius: 6px
			background: colors.$black
			color: colors.$white
			font-size: 15px
			text-align: right

			&::placeholder
				color: colors.$grey-50

	/* — expandable wish list — */
	.marklist
		border: 1px solid colors.$grey-20
		border-radius: 8px
		background: #111111
		overflow: hidden

		button
			margin: 0
			border: 0
			border-radius: 0
			background: transparent
			color: colors.$white
			font-family: inherit
			text-align: left
			cursor: pointer

			&:focus-visible
				outline: 2px solid colors.$primary
				outline-offset: -2px

	.toggle
		display: flex
		align-items: center
		gap: 8px
		width: 100%
		min-height: 44px
		padding: 0 12px

		.count
			padding: 0 7px
			border-radius: 9px
			background: colors.$grey-20
			color: colors.$grey-85
			font-size: 11px

		.summary
			flex: 1
			min-width: 0
			overflow: hidden
			font-size: 12px
			color: colors.$grey-60
			white-space: nowrap
			text-overflow: ellipsis

		.fa
			color: colors.$grey-60
			transition: transform .15s

			&.open
				transform: rotate(180deg)

	.nothing
		margin: 0
		padding: 4px 12px 12px
		font-size: 13px
		font-style: italic
		color: colors.$grey-60

	.items
		display: block
		width: 100%
		padding: 0 12px 10px

		&:hover .show
			color: colors.$white

	.rows
		display: flex
		flex-direction: column
		border-top: 1px solid colors.$grey-20

	.row
		display: flex
		align-items: center
		gap: 8px
		min-height: 36px
		border-bottom: 1px solid colors.$grey-15

	.tag
		flex: none
		width: 74px
		padding: 1px 0
		border-radius: 20px
		font-size: 11px
		text-align: center
		border: 1px solid colors.$grey-30
		background: colors.$grey-20
		color: colors.$grey-85

		&.found
			border-color: colors.$primary-100
			background: colors.$primary-040-black
			color: colors.$white

		&.companion
			border-color: colors.$highlight-yellow-100
			background: colors.$highlight-yellow-040-black
			color: colors.$white

	.itemname
		flex: 1
		min-width: 0
		overflow: hidden
		font-size: 14px
		white-space: nowrap
		text-overflow: ellipsis

	.price
		font-size: 13px
		color: colors.$grey-85
		white-space: nowrap

	.show
		display: flex
		align-items: center
		justify-content: flex-end
		gap: 6px
		padding-top: 8px
		font-size: 13px
		color: colors.$primary

	.loot
		display: flex
		align-items: center
		justify-content: center
		gap: 8px
		min-height: 44px
		margin: 0
		border: 1px solid colors.$grey-50
		border-radius: 8px
		background: transparent
		color: colors.$grey-85
		font-size: 14px
		cursor: pointer

		&:hover
			color: colors.$white
</style>
